import React, {useMemo} from 'react';
import {Alert, Box, Stack, styled} from '@mui/material';
import {Body, designSystemToken, Icon} from '@lightricks/react-design-system';
import NotificationBannerState from './NotificationBannerState';

// eslint-disable-next-line @typescript-eslint/naming-convention
enum NotificationContentType {
  TITLE_AND_DESCRIPTION = 'titleAndDescription',
  CUSTOM_RENDER = 'customRender',
}

interface NotificationContent {
  type: NotificationContentType.TITLE_AND_DESCRIPTION;
  title: string;
  description: string;
  inline?: boolean;
}

interface NotificationCustomRenderContent {
  type: NotificationContentType.CUSTOM_RENDER;
  customRender: () => React.ReactNode;
}

interface NotificationBannerProps {
  testID?: string;
  state: NotificationBannerState;
  content: NotificationCustomRenderContent | NotificationContent;
  onRemove?: (event: React.MouseEvent<HTMLElement>) => void;
}

const IconContainer = styled(Box)(() => ({
  cursor: 'pointer',
}));

function NotificationBanner({
  testID = 'notification-banner',
  state,
  content,
  onRemove,
}: NotificationBannerProps) {
  const getAlertSeverity = () => {
    switch (state) {
      case NotificationBannerState.SUCCESS:
        return 'success';
      case NotificationBannerState.ERROR:
        return 'error';
      case NotificationBannerState.INFO:
        return 'info';
      case NotificationBannerState.WARNING:
        return 'warning';
      default:
        return 'info';
    }
  };

  const getBackgroundColor = () => {
    switch (state) {
      case NotificationBannerState.SUCCESS:
        return designSystemToken('semantic.bg.success-secondary');
      case NotificationBannerState.ERROR:
        return designSystemToken('semantic.bg.danger-secondary');
      case NotificationBannerState.INFO:
        return designSystemToken('semantic.bg.brand-secondary');
      case NotificationBannerState.WARNING:
        return designSystemToken('semantic.bg.warning-secondary');
      default:
        return designSystemToken('semantic.bg.secondary');
    }
  };

  const getTextColor = () => {
    switch (state) {
      case NotificationBannerState.SUCCESS:
        return designSystemToken('semantic.fg.success');
      case NotificationBannerState.ERROR:
        return designSystemToken('semantic.fg.danger');
      case NotificationBannerState.INFO:
        return designSystemToken('semantic.fg.brand');
      case NotificationBannerState.WARNING:
        return designSystemToken('semantic.fg.warning');
      default:
        return designSystemToken('semantic.fg.secondary');
    }
  };

  const iconMapping = useMemo(() => {
    const icon = (
      <Icon
        appearance="neutral"
        size="medium"
        name="Actions-Info-Fill"
        color={getTextColor()}
      />
    );
    return {
      success: <IconContainer onClick={onRemove}>{icon}</IconContainer>,
      error: <IconContainer onClick={onRemove}>{icon}</IconContainer>,
      info: <IconContainer onClick={onRemove}>{icon}</IconContainer>,
      warning: <IconContainer onClick={onRemove}>{icon}</IconContainer>,
    };
  }, [state, onRemove]);

  const renderContent = () => {
    switch (content.type) {
      case NotificationContentType.CUSTOM_RENDER:
        return content.customRender();
      case NotificationContentType.TITLE_AND_DESCRIPTION:
        if (content.inline) {
          return (
            <Body
              size="sm"
              color={getTextColor()}
              sx={{
                '& strong': {
                  fontWeight: 700,
                },
              }}
            >
              <strong>{content.title}</strong> {content.description}
            </Body>
          );
        }

        return (
          <>
            <Body
              size="sm"
              color={getTextColor()}
              sx={{
                fontWeight: 700,
              }}
            >
              {content.title}
            </Body>
            <Body size="sm" color={getTextColor()}>
              {content.description}
            </Body>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Alert
      data-testid={testID}
      iconMapping={iconMapping}
      severity={getAlertSeverity()}
      variant="standard"
      sx={{
        'backgroundColor': getBackgroundColor(),
        'padding': '8px 12px',
        'borderRadius': '12px',
        'border': 'none',
        '& .MuiAlert-icon': {
          marginRight: '8px',
          color: getTextColor(),
        },
        '& .MuiAlert-message': {
          width: '100%',
          alignContent: 'center',
        },
      }}
    >
      <Stack direction="column">{renderContent()}</Stack>
    </Alert>
  );
}

export default NotificationBanner;
export {NotificationContentType};
export type {NotificationContent, NotificationCustomRenderContent};
